import React, { useContext, Fragment, useEffect, useState } from 'react';
import { AuthContext } from '../App';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Home, BookOpen, Calendar, TrendingUp, Shield, LogOut, Trophy, Menu, MessageCircle, Users, Youtube, Mail, DollarSign, Library, Activity, Send, KeyRound, Settings, Search, Star } from 'lucide-react';
import { NotificationBell } from './NotificationBell';
import GlobalSearch from './GlobalSearch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Navigation = () => {
  const { user, logout, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [hasSecurityQuestions, setHasSecurityQuestions] = useState(true); // Optimistically hide until we know
  const [searchOpen, setSearchOpen] = useState(false);

  // Cmd/Ctrl+K anywhere in the app opens the global search.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    // Only logged-in users have security questions status.
    if (!user) {
      setHasSecurityQuestions(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/security-questions/status`);
        if (!cancelled) {
          setHasSecurityQuestions(!!res.data?.has_security_questions);
        }
      } catch {
        // On error, err on the side of showing the icon so the user can act.
        if (!cancelled) setHasSecurityQuestions(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, API, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/discipleship', label: 'Path', icon: TrendingUp },
    { path: '/journal', label: 'Vital Signs Journal', icon: Activity },
    { path: '/sermon', label: 'Sermon Notes', icon: BookOpen },
    { path: '/prayers', label: 'Prayer Wall', icon: MessageCircle },
    { path: '/livestream', label: 'Media', icon: Youtube },
    { path: '/resources', label: 'Resources', icon: Library },
    { path: '/gamification', label: 'Tracker', icon: Trophy },
    { path: '/favorites', label: 'My Collection', icon: Star },
  ];

  const adminItems = [
    { path: '/admin', label: 'Admin Dashboard', icon: Shield },
    { path: '/admin/messages', label: 'Send Messages', icon: Mail },
  ];

  if (user?.role === 'admin' || user?.is_prayer_moderator) {
    navItems.push({
      path: '/admin',
      label: user?.role === 'admin' ? 'Admin' : 'Prayer Admin',
      icon: Shield,
    });
  }

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white border-b border-slate-200 shadow-sm" data-testid="navigation">
      <div className="container mx-auto px-2 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between h-16 gap-1 sm:gap-2">
          {/* Logo/Title */}
          <div 
            className="flex items-center gap-2 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity min-w-0"
            onClick={() => navigate('/dashboard')}
            data-testid="logo-home-link"
          >
            <img 
              src="/wfmc-logo.png" 
              alt="WFMC Logo" 
              className="h-9 w-auto flex-shrink-0"
            />
            <span className="font-heading text-base lg:text-lg font-bold text-[#2B6B7F] hidden lg:block whitespace-nowrap">
              Discipleship Journey
            </span>
          </div>

          {/* Navigation Dropdown - Always visible */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Messages Button - Icon only */}
            <Button
              onClick={() => navigate('/messages')}
              variant="outline"
              className="border-blue-500 text-blue-600 hover:bg-blue-50 h-9 w-9"
              size="icon"
              title="Messages"
            >
              <Mail className="w-4 h-4" />
            </Button>

            {/* Giving Button - Icon only */}
            <Button
              onClick={() => navigate('/giving')}
              className="bg-green-600 hover:bg-green-700 text-white h-9 w-9"
              size="icon"
              title="Give"
            >
              <DollarSign className="w-4 h-4" />
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="flex items-center gap-1.5 px-2 sm:px-3 h-9">
                  <Menu className="w-4 h-4" />
                  <span className="text-sm">Menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {navItems.map((item, index) => {
                  const Icon = item.icon;
                  
                  return (
                    <DropdownMenuItem
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`flex items-center gap-3 cursor-pointer ${
                        isActive(item.path) ? 'bg-[#2B6B7F]/10 text-[#2B6B7F]' : ''
                      }`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </DropdownMenuItem>
                  );
                })}
                
                {/* Contact Admin - Available to all users */}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/contact-admin')}
                  className={`flex items-center gap-3 cursor-pointer ${
                    isActive('/contact-admin') ? 'bg-[#2B6B7F]/10 text-[#2B6B7F]' : ''
                  }`}
                  data-testid="nav-contact-admin"
                >
                  <Send className="w-4 h-4" />
                  <span>Message Admin</span>
                </DropdownMenuItem>
                
                {/* Admin Section */}
                {user?.role === 'admin' && (
                  <>
                    <DropdownMenuSeparator />
                    {adminItems.map((item) => {
                      const Icon = item.icon;
                      // Show "Send Messages" only for Jason Owens
                      if (item.path === '/admin/messages' && user?.email !== 'jason@westlandfmc.com') {
                        return null;
                      }
                      
                      return (
                        <DropdownMenuItem
                          key={item.path}
                          onClick={() => navigate(item.path)}
                          className={`flex items-center gap-3 cursor-pointer ${
                            isActive(item.path) ? 'bg-[#2B6B7F]/10 text-[#2B6B7F]' : ''
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </DropdownMenuItem>
                      );
                    })}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
            <button
              onClick={() => setSearchOpen(true)}
              className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              title="Search the app (Ctrl/Cmd+K)"
              data-testid="nav-search"
            >
              <Search className="w-4 h-4 text-slate-600" />
            </button>
            {!hasSecurityQuestions && (
              <button
                onClick={() => navigate('/security-questions')}
                className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                title="Set up Security Questions"
                data-testid="nav-security"
              >
                <KeyRound className="w-4 h-4 text-slate-600" />
              </button>
            )}
            <NotificationBell onNavigate={(link) => navigate(link)} />
            <button
              onClick={() => navigate('/notifications')}
              className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              title="Notification Settings"
              data-testid="nav-notification-settings"
            >
              <Settings className="w-4 h-4 text-slate-600" />
            </button>
            <span className="text-xs text-slate-600 hidden lg:block max-w-[80px] truncate ml-1">
              {user?.name}
            </span>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="icon"
              className="border-slate-300 h-8 w-8 ml-0.5"
              title="Logout"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} user={user} />
    </nav>
  );
};

export default Navigation;
